import { NavLink, useNavigate } from 'react-router-dom';
import {
  Building2,
  Home,
  LayoutGrid,
  Laptop,
  Rocket,
  Settings,
  Users,
} from 'lucide-react';

import AirBridgeMark from './AirBridgeMark';
import TopographyTexture from './TopographyTexture';
import { BRAND_LABEL, TAGLINE, WORDMARK } from '../lib/brand';
import { LAYOUT, NAV, NAV_DEPTH, PLAIN_BUTTON } from '../theme/tokens';

// The AirBridge left rail — deep navy, full height, 240px (FD-ADR-043 §1.6).
//
// This replaces BOTH halves of the ADR-031 shell at once: the dark top bar's
// brand anchor moves here, and the sectioned nav tree becomes a flat grouped
// list. The tree's expand-in-place group→campaign drill-down is NOT reproduced;
// the mockups navigate to a Groups page and drill from there, which is one
// fewer place for the same hierarchy to be stated.
//
// WHAT IS DELIBERATELY ABSENT (FD-ADR-043 D4): the mockups' **Automation** and
// **Reports** sections, and the **Flight Plans** item under Migration. There is
// no backend behind any of them. Shipping a nav item that leads nowhere teaches
// an operator that the console lies — the same argument TopBar.tsx made when it
// refused to ship a mock search box.
//
// ACTIVE ROW: white text, a brand wash and a 3px brand indicator bar. NOT
// brand-coloured text — `#1677F2` on `#061B2C` is 4.13:1, below AA (see
// theme/contrast.md). The indicator carries the colour; the label stays white.
//
// DEPTH (FD-487). Three decorative layers sit behind the content, all of them
// from `NAV_DEPTH` and none of them carrying meaning: a vertical ground
// gradient, a sky glow behind the mark, and the topographic contour texture in
// `TopographyTexture`. They stack in paint order under a `relative`/`z-10` content
// layer, and the rail clips them with `overflow-hidden` so the texture cannot
// bleed into the content column.
//
// The arithmetic that makes them safe behind text is in `TopographyTexture.tsx` and
// in `NAV_DEPTH`'s doc comments — computed, not estimated. The short version:
// the rail's published 17.49 / 9.89 ratios stay the worst case, so nothing here
// touches the cross-repo token contract.

interface RailItem {
  to: string;
  label: string;
  icon: typeof Home;
  /** Exact match only — for a path that is a prefix of its own children. */
  end?: boolean;
}

interface RailSection {
  heading: string | null;
  items: RailItem[];
}

// ABSOLUTE paths, not relative. The rail is mounted by `AppShell`, which is a
// LAYOUT route with no path of its own — so a relative `to` would resolve
// against whatever the operator is currently looking at and the rail would
// navigate somewhere different from every page.
//
// "Campaigns" points at `/migrations`, and that is deliberate rather than an
// oversight. FD-ADR-031 §3's rule is that the operator vocabulary and the wire
// are different things: the LABEL is the operator's word, the PATH is an
// existing address that bookmarks, links and `NotFound` already use. Renaming
// the route would break every one of those to change a string nobody reads.
const SECTIONS: RailSection[] = [
  { heading: null, items: [{ to: '/home', label: 'Home', icon: Home, end: true }] },
  {
    heading: 'Migration',
    items: [
      // Campaigns first (FD-480). A campaign is the unit of work and the thing
      // an operator opens every day; a group is an OPTIONAL container over
      // several of them, inert with respect to execution (FD-ADR-030 §3). The
      // rarer, optional thing does not lead the section.
      { to: '/migrations', label: 'Campaigns', icon: Rocket },
      { to: '/groups', label: 'Groups', icon: LayoutGrid },
    ],
  },
  {
    heading: 'Resources',
    items: [
      { to: '/tenants', label: 'Tenants', icon: Building2 },
      { to: '/devices', label: 'Devices', icon: Laptop },
      { to: '/identities', label: 'Identities', icon: Users },
    ],
  },
  {
    heading: 'Deployment',
    items: [{ to: '/settings', label: 'Settings', icon: Settings }],
  },
];

export default function Rail() {
  const navigate = useNavigate();

  return (
    <nav
      aria-label="Primary"
      className="relative flex shrink-0 flex-col overflow-hidden"
      style={{
        width: LAYOUT.railWidth,
        // `backgroundColor` is the floor under the gradient, not a duplicate of
        // it: a gradient that fails to parse would otherwise leave the rail
        // transparent over the canvas rather than merely flat.
        backgroundColor: NAV.deep,
        backgroundImage: NAV_DEPTH.rail,
      }}
    >
      <TopographyTexture variant="rail" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-48"
        style={{ backgroundImage: NAV_DEPTH.brandGlow }}
      />

      {/* Brand. A button rather than a link so it matches the top-bar
          convention of every admin console; PLAIN_BUTTON because Tailwind
          preflight is off and a bare <button> would render the browser's own
          light box on the navy. */}
      <button
        type="button"
        onClick={() => navigate('/home')}
        aria-label={BRAND_LABEL}
        className={`${PLAIN_BUTTON} relative z-10 flex items-center gap-2.5 px-5 py-5 text-left focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-white/50`}
      >
        <AirBridgeMark size={30} />
        <span className="flex min-w-0 flex-col">
          <span
            className="text-[17px] font-semibold leading-none"
            style={{ color: NAV.text }}
          >
            {WORDMARK}
          </span>
          <span
            className="mt-1 text-[8px] font-medium uppercase leading-none"
            style={{ color: NAV.textMuted, letterSpacing: '0.11em' }}
          >
            {TAGLINE}
          </span>
        </span>
      </button>

      <div className="relative z-10 min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {SECTIONS.map((section) => (
          <div key={section.heading ?? 'root'} className="mb-1">
            {section.heading && (
              <div
                className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase"
                style={{ color: NAV.textMuted, letterSpacing: '0.1em' }}
              >
                {section.heading}
              </div>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className="relative mb-0.5 flex items-center gap-2.5 rounded-md py-2 pl-3 pr-2 text-[13px] no-underline transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? NAV.text : NAV.textMuted,
                  // A gradient, so `backgroundImage` rather than
                  // `backgroundColor` — `NAV.activeFill` stays the flat answer
                  // for dark surfaces that are not the rail.
                  backgroundImage: isActive ? NAV_DEPTH.activeRow : 'none',
                  fontWeight: isActive ? 600 : 400,
                })}
              >
                {({ isActive }) => (
                  <>
                    {/* The indicator carries the brand colour; the label never
                        does. See the contrast note at the top of this file. */}
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
                      style={{
                        backgroundImage: isActive
                          ? NAV_DEPTH.activeIndicator
                          : 'none',
                      }}
                    />
                    <item.icon size={16} strokeWidth={1.5} />
                    <span className="truncate">{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      {/* NO IDENTITY BLOCK HERE (FD-481). The mockups draw one at the foot of
          the rail and it shipped that way; on the deployed navy it rendered as
          a floating avatar beside an unreadable label, and it is now at the
          right of the top bar where Azure-portal muscle memory expects it.
          `Resources > Identities` above is a different thing entirely — the
          identity-MAPPING workspace — and stays exactly where it is. */}
    </nav>
  );
}
